import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import ToggleButton from '@material-ui/lab/ToggleButton'
import WbSunnyOutlinedIcon from '@material-ui/icons/WbSunnyOutlined'
import { useDispatch, useSelector } from 'react-redux'
import { IRootReduxState, IUserState } from 'types/reducers'
import { toggleTheme } from 'reducers/userReducer'
import LanguagePanel from 'components/LanguagePanel'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import DarkMode from 'assets/images/svgs/dark_mode.svg'
import { FormattedMessage } from 'react-intl'

const useStyles = makeStyles((theme) => ({
    page: {
        padding: 32,
        maxWidth: 700,
        margin: '0 auto',
    },

    section: {
        padding: 24,
        marginBottom: 24,
        borderRadius: 8,
        background: 'rgb(6, 130, 192)',
    },

    title: {
        fontWeight: 600,
        marginBottom: 16,
        color: '#fff'
    },

    row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 20,
        color: '#fff'
    },

    toggleGroup: {
        background: theme.palette.grey[200],
        borderRadius: 999,
        padding: 4,
    },

    toggleBtn: {
        border: 'none',
        borderRadius: 999,
        padding: '6px 18px',
        minWidth: 110,
        fontWeight: 500,
        color: theme.palette.primary.dark, // default icon color

        '&.Mui-selected': {
            background: theme.palette.primary.main,
            color: '#0b2b3b', // ✅ selected icon becomes dark (not white)
        },

        '&.Mui-selected:hover': {
            background: theme.palette.primary.dark,
            color: '#fff',
        },
    },
}))

const GlobalSettingsPage: React.FC = () => {
    const classes = useStyles()
    const dispatch = useDispatch()
    const userInfo = useSelector<IRootReduxState, IUserState>(
        (state) => state.app.user
    )

    return (
        <div className={classes.page}>
            {/* Theme Section */}
            <Paper elevation={2} className={classes.section}>
                <Typography variant="h6" className={classes.title}>
                    <FormattedMessage id="appearance" defaultMessage="Appearance" />
                </Typography>

                <div className={classes.row}>
                    <Typography>
                        <FormattedMessage id="themeMode" defaultMessage="Theme Mode" />
                    </Typography>

                    <ToggleButtonGroup
                        value={userInfo.theme === 'light' ? 'light' : 'dark'}
                        exclusive
                        onChange={(_, val) => {
                            if (!val) return
                            const nextIsLight = val === 'light'
                            const currentlyLight = userInfo.theme === 'light'
                            if (nextIsLight !== currentlyLight) dispatch(toggleTheme())
                        }}
                        className={classes.toggleGroup}
                    >
                        <ToggleButton value="light" className={classes.toggleBtn}>
                            <WbSunnyOutlinedIcon fontSize="small" />
                        </ToggleButton>

                        <ToggleButton value="dark" className={classes.toggleBtn}>
                            <img src={DarkMode} alt="dark Mode" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </div>
            </Paper>

            {/* Language Section */}
            <Paper elevation={2} className={classes.section}>
                <Typography variant="h6" className={classes.title}>
                    <FormattedMessage id="language" defaultMessage="Language" />
                </Typography>

                <div className={classes.row}>
                    <Typography>
                        <FormattedMessage id="applicationLanguage" defaultMessage="Application Language" />
                    </Typography>
                    <LanguagePanel />
                </div>
            </Paper>
        </div>
    )
}

export default GlobalSettingsPage